/**
 * Jam-recovery prompt copy (LOCKED-DESIGN 18.16 / PLAYTEST F10).
 *
 * F10 recorded a single jam as an *absorbing state*: one 7 %-chance roll froze
 * scoring for the final 24 % of a shift. The cheapest mitigation on paper was
 * "your lane is stuck — throw the other ring down on TECHNICAL and keep
 * scoring". **That escape valve does not exist.** Verified by running the sim:
 * `Simulation.processPlacements()` holds EVERY queued placement while
 * `anyLaneJammed()` (the Section 3.3 reconfiguration interlock), so with safe
 * jammed, `place(orange, "technical")` returns a `portal-armed` event and never
 * a `portal-swapped` — 200 ticks later `activeRoute` is still `{floor, safe}`
 * and every subsequent load resolves `delivered: 0`. In Simple it is worse:
 * Q/R are inert and `maybeIssueSimplePortalMove` only ever issues BLUE places,
 * so the receiver is pinned to `safe` for the whole run.
 *
 * The interlock exemption that would make the escape valve real is a KERNEL
 * change riding a fixture re-export; it was surfaced to the owner and NOT
 * approved (LOCKED-DESIGN 18.14 "Deferred to the owner"). So this copy states
 * the options the player actually has — jump in and feed the pile to the maw —
 * and, for adults, names the RULE that removed the other option so a locked
 * portal reads as a mechanic and not as player error.
 *
 * **What the kernel DOES allow, and what this copy used to get wrong (18.16
 * amendment).** `release()` gates on `laneJammed(route.receiver)` — the load's
 * OWN lane — not on `anyLaneJammed()`. So when the route the interlock froze
 * points at a lane that is NOT the jammed one, every load down it still resolves
 * and still scores at full value. That state is ordinary adult play: swap the
 * orange endpoint, then the load already in flight to the old receiver resolves
 * missed and jams it. Proven by running the sim (`JamRecoveryCopy.test.ts`):
 * safe jammed at 6 bulk, route frozen at floor→technical, next release resolves
 * `clean` for 1,437 points. The blanket "NO POINTS, JUST RECOVERY" was therefore
 * a lie in exactly that state, and told a player who could still score to stop.
 * `openReceiver` names that live lane instead. It is NOT the forbidden escape
 * valve: it never promises a lane the player would have to SWITCH to — only the
 * one the kernel already has them pointed at.
 *
 * Pure by design: no DOM, no engine, no clock, so every string is unit-pinned
 * and a future exemption changes exactly one file.
 */
import type { ReceiverTier } from "../simulation/types.js";

export interface JamRecoveryPrompt {
  /** Reticle-dock headline: the verb chain that actually clears the jam. */
  text: string;
  /** Quieter second line naming the interlock; null in Simple (no jargon for kids). */
  detail: string | null;
  /** Escalated styling — the player kept feeding a lane that is already jammed. */
  urgent: boolean;
}

/**
 * The consequence line. Adult-only, and deliberately phrased as a property of
 * the machine ("is interlocked") rather than of the player ("you can't").
 */
export const INTERLOCK_NOTE = "(PORTAL RECONFIGURATION IS INTERLOCKED WHILE A LANE IS JAMMED)";

/**
 * Adult world tag at the live blue entrance ring — the only real action.
 *
 * The verb is JUMP since 18.25. The intent gate (PLAYTEST F7 / open question 8)
 * makes an airborne or dashing approach the only way through a ring into the
 * chamber, so "RIDE IN" now describes something a standing player cannot do.
 * Recovery is the mode's most-failed loop; its copy may not name a dead verb.
 */
export const JAM_ENTRANCE_TAG = "OPEN · JUMP IN HERE";
/** Same cue as an off-frame chevron label (shorter: the flare is narrow). */
export const JAM_ENTRANCE_FLARE = "JUMP IN HERE";

/**
 * The recovery prompt for a run with `lane` jammed.
 *
 * `extending` is the ninth-dogfood re-jam signal (a player kept releasing into
 * THIS lane and the pile outgrew their feeding); its STOP-feeding copy is
 * composed with, never replaced by, the interlock line. It is per-lane on
 * purpose: a global flag names a lane the player never fed the moment a second
 * jam exists and the prompt moves on to it.
 *
 * `othersJammed` is how many OTHER lanes are jammed at the same time (multi-jam
 * is reachable: two releases one tick apart resolve into two lanes). The
 * Section 3.3 interlock lifts when NO lane is jammed, so with another lane
 * still stuck "until IT clears" would be a lie.
 *
 * `openReceiver` is the player's LIVE route receiver when the interlock froze it
 * on a lane that is not jammed and the phase still accepts it — the one lane the
 * kernel keeps paying. `null` (the common case, and always in Simple) restores
 * the byte-identical "NO POINTS, JUST RECOVERY" copy, which is true only then.
 */
export function jamRecoveryPrompt(input: {
  lane: ReceiverTier;
  simple: boolean;
  extending: boolean;
  othersJammed?: number;
  openReceiver?: ReceiverTier | null;
}): JamRecoveryPrompt {
  const { lane, simple, extending } = input;
  const others = Math.max(0, input.othersJammed ?? 0);
  const name = lane.toUpperCase();
  // A jammed lane is never its own escape hatch; the Engine cannot produce this,
  // but the rule must not depend on the caller for a truthfulness guarantee.
  const open = input.openReceiver && input.openReceiver !== lane ? input.openReceiver : null;
  if (simple) {
    // Kid copy (LOCKED-DESIGN 18.11): no lane names, no "interlock", no
    // promise of a second ring — in Simple there is only ever one receiver and
    // the kernel would refuse to move it anyway. `openReceiver` is ignored here
    // rather than translated: Q/R are inert in Simple and the auto-mover issues
    // only BLUE places, so the receiver is pinned to the one that jammed and an
    // open lane cannot exist. Kid copy is byte-unchanged by this amendment.
    return {
      text: extending
        ? "STOP! DON'T THROW MORE HERE — JUMP IN AND FEED THE FIRE FIRST!"
        : "STUCK! THE RING IS BLOCKED — JUMP IN AND FEED THE FIRE!",
      detail: null,
      urgent: extending,
    };
  }
  const unlock = others > 0 ? "UNTIL EVERY JAMMED LANE CLEARS" : "UNTIL IT CLEARS";
  // The one true consequence line. With no open lane the jam really is a total
  // scoring stop; with one, the honest line is that the frozen route still pays.
  // Hazard carries its baffle in the same breath — a hazard load released into a
  // blocked throat is deflected whole and would jam a SECOND lane, so "still
  // pays" without "on the open gate" would trade one lie for another.
  const tail = open === null
    ? "NO POINTS, JUST RECOVERY"
    : `YOUR ${open.toUpperCase()} ROUTE STILL PAYS${open === "hazard" ? " ON THE OPEN GATE" : ""}`;
  return {
    text: extending
      ? `STILL JAMMED · STOP FEEDING ${name} — JUMP IN AND FEED THE PILE TO THE MAW${open === null ? "" : ` · ${tail}`}`
      : `${name} IS JAMMED · ROUTE CHANGES ARE LOCKED ${unlock} — JUMP IN AND FEED THE PILE TO THE MAW · ${tail}`,
    detail: INTERLOCK_NOTE,
    urgent: extending,
  };
}
